
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Mail, Phone, MapPin } from "lucide-react";

interface LegalInformationCardProps {
  privacyPolicyContent?: string;
  termsOfServiceContent?: string;
  privacyEffectiveDate?: string;
  termsEffectiveDate?: string;
  copyrightStartYear?: number;
  legalEmail?: string;
  legalPhone?: string;
  legalAddress?: string;
  onInputChange: (field: string, value: string | number) => void;
}

export const LegalInformationCard = ({
  privacyPolicyContent,
  termsOfServiceContent,
  privacyEffectiveDate,
  termsEffectiveDate,
  copyrightStartYear,
  legalEmail,
  legalPhone,
  legalAddress,
  onInputChange,
}: LegalInformationCardProps) => {
  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-brand-purple" />
          Legal Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Legal Contact Information Section */}
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-gray-900">Legal Contact Information</h4>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="legal_email">Legal Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="legal_email"
                  type="email"
                  value={legalEmail || ""}
                  onChange={(e) => onInputChange("legal_email", e.target.value)}
                  className="pl-10"
                  placeholder="legal@boboteedee.ky"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="legal_phone">Legal Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="legal_phone"
                  value={legalPhone || ""}
                  onChange={(e) => onInputChange("legal_phone", e.target.value)}
                  className="pl-10"
                  placeholder="+1 (345) 123-4567"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="legal_address">Legal Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Textarea
                  id="legal_address"
                  value={legalAddress || ""}
                  onChange={(e) => onInputChange("legal_address", e.target.value)}
                  className="pl-10 min-h-[60px]"
                  placeholder="Legal Department Address"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Legal Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="privacy_policy_content">Privacy Policy Content</Label>
              <Textarea
                id="privacy_policy_content"
                value={privacyPolicyContent || ""}
                onChange={(e) => onInputChange("privacy_policy_content", e.target.value)}
                className="min-h-[120px]"
                placeholder="Enter your privacy policy content..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="privacy_effective_date">Privacy Policy Effective Date</Label>
              <Input
                id="privacy_effective_date"
                type="date"
                value={privacyEffectiveDate || ""}
                onChange={(e) => onInputChange("privacy_effective_date", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="terms_of_service_content">Terms of Service Content</Label>
              <Textarea
                id="terms_of_service_content"
                value={termsOfServiceContent || ""}
                onChange={(e) => onInputChange("terms_of_service_content", e.target.value)}
                className="min-h-[120px]"
                placeholder="Enter your terms of service content..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="terms_effective_date">Terms of Service Effective Date</Label>
              <Input
                id="terms_effective_date"
                type="date"
                value={termsEffectiveDate || ""}
                onChange={(e) => onInputChange("terms_effective_date", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="copyright_start_year">Copyright Start Year</Label>
          <Input
            id="copyright_start_year"
            type="number"
            min="1900"
            max={new Date().getFullYear()}
            value={copyrightStartYear || new Date().getFullYear()}
            onChange={(e) => onInputChange("copyright_start_year", parseInt(e.target.value) || new Date().getFullYear())}
            className="w-32"
            placeholder="2024"
          />
          <p className="text-sm text-gray-500">
            Used for copyright display (e.g., "© {copyrightStartYear || new Date().getFullYear()}-{new Date().getFullYear()} Your Company")
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
